import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useOnboardingStore } from './store/onboarding-store';
import { StepIndicator } from './store/step-indicator';
import { PayloadBrandContext, StepSelectFunnels } from './steps/step-1-select-funnels';
import { StepBrandConfig } from './steps/step-2-brand-config';
import { useEffect, useState } from 'react';
import { getBrandContext, postCreateHome, CreateHomeData } from '../../services/home/homeService';
import Loader from '../common/Loader';
import { toast } from 'react-hot-toast';
import { dictionary, Language } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export interface PayloadBrandContextComplete {
	websites_info: PayloadBrandContext[];
}

export function OnboardingLayoutHome() {
	const navigate = useNavigate();
	const { language } = useAuth();
	const { data, nextStep, previousStep } = useOnboardingStore();
	const { step, selectedFunnels, brandData } = data;
	const [payloadBrandContext, setPayloadBrandContext] = useState<PayloadBrandContext[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingCreateHome, setLoadingCreateHome] = useState(false);
	const [brandName, setBrandName] = useState<string[]>([]);
	const [brandContext, setBrandContext] = useState<string[]>([]);
	const [selectedBrandName, setSelectedBrandName] = useState(brandName[0] || "")
	const [selectedBrandContext, setSelectedBrandContext] = useState(brandContext[0] || "")

	const renderStepContent = () => {
		switch (step) {
			case 1:
				return <StepSelectFunnels setPayloadBrandContext={setPayloadBrandContext} payloadBrandContext={payloadBrandContext} />;
			case 2:
				return <StepBrandConfig loading={loading} name={brandName} context={brandContext} selectedBrandName={selectedBrandName} selectedBrandContext={selectedBrandContext} setSelectedBrandName={setSelectedBrandName} setSelectedBrandContext={setSelectedBrandContext} />;
			default:
				return <StepSelectFunnels setPayloadBrandContext={setPayloadBrandContext} payloadBrandContext={payloadBrandContext} />;
		}
	};

	const handleNextStep = () => {
		if (selectedFunnels.length === 0) {
			toast.error(dictionary['error-select-funnel'][language]);
			return;
		}
		setLoading(true);
		const data: PayloadBrandContextComplete = {
			websites_info: payloadBrandContext,
		}
		getBrandContext(data)
			.then((response) => {
				setBrandName(response.brands);
				setBrandContext(response.contexts);
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				setLoading(false);
			});
		nextStep();
	}

	const isLastStep = step === 2;
	const canProceed = step === 1 ? selectedFunnels.length > 0 : brandData.name.trim() !== '';

	const handleSubmit = () => {
		if (selectedBrandName.trim() === '' || selectedBrandContext.trim() === '') {
			toast.error(dictionary['error-brand-data'][language] || "Por favor, completa el nombre de la marca y el contexto");
			return;
		}
		setLoadingCreateHome(true);
		const data: CreateHomeData = {
			brand_name: selectedBrandName,
			context: selectedBrandContext,
			funnels: selectedFunnels.map((funnel) => funnel.id),
			configurations: {
				enabled: true
			}
		}
		postCreateHome(data)
			.then((response) => {
				toast.success(dictionary['success-create-home'][language]);
				setTimeout(() => {
					navigate("/funnels?type=homes")
				}, 1000);
			})
			.catch((error) => {
				toast.error(dictionary['error-create-home'][language]);
			})
			.finally(() => {
				setLoadingCreateHome(false);
			});
	};

	const stepTitles = ['Seleccionar Embudos', 'Configurar Marca'];

	const [isButtonDisabled, setIsButtonDisabled] = useState(false);

	useEffect(() => {
		const isBrandDataEmpty = selectedBrandName.trim() === '' || selectedBrandContext.trim() === '';
		const isButtonDisabled = isLastStep ? loadingCreateHome || isBrandDataEmpty : false;
		setIsButtonDisabled(isButtonDisabled);
	}, [selectedBrandName, selectedBrandContext, loadingCreateHome, isLastStep]);

	return (
		<div className="max-w-5xl mx-auto px-4 py-8">
			<h1 className="text-2xl font-bold mb-6 flex items-center justify-center gap-2">
				<img
					src="https://www.svgrepo.com/show/190530/magic-wand-wizard.svg"
					alt="Varita mágica"
					className="w-7 h-7"
				/>
				{dictionary["group-funnels-and-create-homes"]?.[language as Language] ?? "Agrupa embudos y crea tu tienda en segundos"}
			</h1>

			<div className="mb-6">
				{/* Step indicators with connector lines - full width layout */}
				<div className="w-full flex items-center justify-center relative">
					{/* Connector line between steps */}
					<div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-32 h-0.5 z-0">
						<div
							className={`h-0.5 w-full ${step > 1 ? 'bg-primary-600' : 'bg-gray-300'}`}
						></div>
					</div>

					{[1, 2].map((stepNumber, index) => (
						<div key={stepNumber} className={`flex flex-col items-center relative z-10 ${index === 0 ? 'mr-32' : ''}`}>
							<StepIndicator
								step={stepNumber}
								currentStep={step}
							/>
						</div>
					))}
				</div>
			</div>

			<div className="bg-white rounded-xl shadow-lg p-6 mb-8">
				{renderStepContent()}
			</div>

			<div className="flex justify-between">
				{step > 1 ? (
					<button
						type="button"
						onClick={previousStep}
						className="btn btn-secondary"
					>
						<ArrowLeft className="w-5 h-5 mr-2" />
						{dictionary["previous"]?.[language as Language] ?? "Anterior"}
					</button>
				) : (
					<div></div>
				)}

				<button
					type="button"
					onClick={isLastStep ? handleSubmit : handleNextStep}
					className={`btn ${isButtonDisabled ? 'btn-primary opacity-50 cursor-not-allowed' : 'btn-primary'}`}
					disabled={isButtonDisabled}
				>
					{isLastStep ? (
						loadingCreateHome ? (
							<div className="flex items-center gap-2">
								<Loader />
							</div>
						) : (	
							<>	
								{dictionary["create-store"]?.[language as Language] ?? "Crear tienda"}
							</>
						)
					) : (
						<>
							{dictionary["next"]?.[language] ?? "Siguiente"}
							<ArrowRight className="w-5 h-5 ml-2" />
						</>
					)}
				</button>
			</div>
		</div>
	);
}